import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Megaphone, X } from "lucide-react";
import { Button } from "../../components/ui/button/index.js";
import { SheetDescription, SheetTitle } from "../../components/ui/sheet/index.js";
import { cn } from "../../lib/utils.js";
import {
  contactSheetDescriptionClass,
  contactSheetHeaderClass,
  contactSheetHeaderIconClass,
  contactSheetTitleClass,
} from "../contacts/contact-sheet-presentation.js";
import {
  CAMPAIGN_LIST_NAV_ITEMS,
  campaignListHref,
  campaignListNavCount,
} from "./campaigns-presentation.js";
import { CampaignProcessingIcon } from "./CampaignProcessingIcon.js";
import { useCampaignWorkspace } from "./CampaignWorkspaceLayout.js";

function campaignDrawerNavItemClass(selected: boolean): string {
  return cn(
    "group flex min-h-11 w-full items-center gap-3 rounded-lg border-s-2 px-3 py-2.5 text-start no-underline transition-colors duration-150 motion-reduce:transition-none",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent/40",
    selected
      ? "border-accent bg-surface-selected font-semibold text-foreground"
      : "border-transparent font-medium text-foreground/80 hover:bg-surface-hover",
  );
}

export function CampaignSubNav({
  onNavigate,
  onClose,
}: {
  onNavigate?: () => void;
  onClose?: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { list } = useCampaignWorkspace();

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-surface">
      <header className={contactSheetHeaderClass}>
        <div className="flex min-w-0 items-start gap-3">
          <span className={contactSheetHeaderIconClass} aria-hidden="true">
            <Megaphone size={16} />
          </span>
          <div className="grid min-w-0 gap-1">
            <SheetTitle
              className={cn(
                contactSheetTitleClass,
                "text-[1.0625rem] leading-tight tracking-[-0.02em]",
              )}
            >
              {t("campaigns.title")}
            </SheetTitle>
            <SheetDescription className={contactSheetDescriptionClass}>
              {t("campaigns.nav.drawerDescription")}
            </SheetDescription>
          </div>
        </div>
        {onClose ? (
          <Button
            type="button"
            variant="ghost"
            className="size-8 shrink-0 p-0 text-muted hover:text-foreground"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            <X className="size-4" aria-hidden="true" />
          </Button>
        ) : null}
      </header>

      <nav
        className="min-h-0 flex-1 overflow-y-auto px-3 py-4"
        aria-label={t("campaigns.nav.filters")}
        data-testid="campaign-status-nav"
      >
        <ul className="m-0 grid list-none gap-1.5 p-0" data-testid="campaign-subnav-list">
          {CAMPAIGN_LIST_NAV_ITEMS.map((item) => {
            const selected = list.status === item.status;
            const count = campaignListNavCount(list.statusCounts, item.countKey);
            const label = t(item.labelKey);
            const Icon = item.icon;
            const tooltip = `${label} (${count})`;
            return (
              <li key={item.id}>
                <Link
                  to={campaignListHref({
                    status: item.status,
                    search: list.search,
                    page: 1,
                    pageSize: list.pageSize,
                    sort: list.sort,
                  })}
                  onClick={onNavigate}
                  className={campaignDrawerNavItemClass(selected)}
                  aria-current={selected ? "page" : undefined}
                  aria-label={tooltip}
                  data-selected={selected ? "true" : "false"}
                  data-testid={item.testId}
                >
                  <span
                    className={cn(
                      "inline-grid size-8 shrink-0 place-items-center rounded-lg",
                      selected
                        ? "bg-accent-soft text-accent"
                        : "bg-surface-muted text-muted group-hover:text-foreground",
                    )}
                  >
                    {item.status === "RUNNING" && count > 0 ? (
                      <CampaignProcessingIcon size={16} />
                    ) : (
                      <Icon size={16} aria-hidden="true" />
                    )}
                  </span>
                  <span className="min-w-0 flex-1 text-[0.875rem] leading-5">{label}</span>
                  <span
                    className={cn(
                      "inline-flex min-w-7 shrink-0 items-center justify-center rounded-full px-2 text-[0.8125rem] font-bold tabular-nums leading-5",
                      selected ? "bg-accent-soft text-accent" : "bg-surface-muted text-muted",
                    )}
                    aria-hidden="true"
                  >
                    {count}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
